/**
 * Settings → Integrations. Microsoft Planner: connect with a Microsoft 365
 * work/school account, choose plan + bucket, switch sync on
 * (services/ms_planner.py on the backend).
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, RefreshCw, CheckCircle2, AlertTriangle, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";

function ago(iso) {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`;
}

function PlannerLogo() {
  return (
    <div className="w-11 h-11 rounded-lg bg-[#31752F] grid place-items-center shrink-0">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 12l2.5 2.5L16 9" />
      </svg>
    </div>
  );
}

export default function Integrations() {
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const [st, setSt] = useState(null);
  const [plans, setPlans] = useState(null);
  const [buckets, setBuckets] = useState(null);
  const [busy, setBusy] = useState(false);
  const [includeExisting, setIncludeExisting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/integrations/microsoft");
      setSt(r.data);
      return r.data;
    } catch { setSt({ configured: false, connected: false }); return null; }
  };

  useEffect(() => {
    const ms = params.get("ms");
    if (ms === "connected") toast.success("Microsoft account connected — now choose a plan");
    if (ms === "error") toast.error(params.get("msg") || "Couldn't connect Microsoft");
    if (ms) { params.delete("ms"); params.delete("msg"); setParams(params, { replace: true }); }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!st?.connected || st.needs_reconnect) return;
    api.get("/integrations/microsoft/plans")
      .then((r) => setPlans(r.data.plans || []))
      .catch((e) => { setPlans([]); toast.error(e?.response?.data?.detail || "Couldn't load your Planner plans"); });
  }, [st?.connected, st?.needs_reconnect]);

  useEffect(() => {
    if (!st?.plan_id) { setBuckets(null); return; }
    api.get("/integrations/microsoft/buckets", { params: { plan_id: st.plan_id } })
      .then((r) => setBuckets(r.data.buckets || []))
      .catch(() => setBuckets([]));
  }, [st?.plan_id]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/microsoft/connect");
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the Microsoft sign-in");
      setBusy(false);
    }
  };

  const save = async (patch, msg) => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/microsoft/settings", patch);
      setSt((cur) => ({ ...cur, ...r.data }));
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const addExisting = async () => {
    const ok = await confirm({ title: "Add your current open tasks to Planner?", message: "All open, approved tasks are added to the chosen plan (about 40 a minute). Review items are not included.", confirmLabel: "Add them" });
    if (!ok) return;
    await save({ include_existing: true });
    await syncNow();
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/microsoft/sync-now");
      const p = r.data.push || {}, q = r.data.pull || {};
      toast.success(`Synced — ${p.created || 0} added, ${p.updated || 0} updated, ${(q.completed || 0) + (q.started || 0)} changes from Planner`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
      load();
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    const ok = await confirm({ title: "Disconnect Microsoft Planner?", message: "Tasks already in Planner stay there; Brainydo stops updating them.", confirmLabel: "Disconnect", tone: "danger" });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post("/integrations/microsoft/disconnect");
      setPlans(null); setBuckets(null);
      await load();
      toast.success("Disconnected");
    } catch { toast.error("Failed to disconnect"); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-3xl mx-auto" data-testid="integrations-page">
      <Link to="/app/settings" className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft size={14} /> Settings</Link>
      <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">Integrations</h1>
      <p className="text-[13.5px] text-slate-500 mt-1 mb-6">Manage Brainydo tasks in the tools your team already uses.</p>

      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="planner-card">
        <div className="flex items-start gap-4">
          <PlannerLogo />
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-bold text-slate-900">Microsoft Planner</div>
            <p className="text-[13px] text-slate-500 mt-0.5">Approved tasks appear in a Planner plan with due date, priority, assignee and the original WhatsApp message. Complete or start them in Planner and Brainydo follows.</p>
          </div>
        </div>

        {!st ? (
          <div className="py-8 grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div>
        ) : !st.configured ? (
          <p className="mt-5 text-[13px] text-amber-700">Microsoft integration isn't set up on this server yet.</p>
        ) : !st.connected ? (
          <div className="mt-5">
            <button onClick={connect} disabled={busy} className="h-10 px-5 bg-[#31752F] hover:bg-[#285f26] text-white rounded-xl text-[13.5px] font-semibold disabled:opacity-60" data-testid="planner-connect">
              {busy ? "Opening Microsoft…" : "Connect Microsoft Planner"}
            </button>
            <p className="text-[12px] text-slate-400 mt-2">Needs a Microsoft 365 work or school account (personal Outlook accounts don't have Planner). Some organisations require their IT admin to approve Brainydo the first time.</p>
          </div>
        ) : (
          <div className="mt-5 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="text-[13px] text-slate-700">
                Connected as <b>{st.ms_user?.name}</b> <span className="text-slate-500">({st.ms_user?.email})</span>
              </div>
              <button onClick={disconnect} disabled={busy} className="h-8 px-3 inline-flex items-center gap-1.5 text-[12.5px] text-slate-600 hover:text-red-600 border border-slate-200 rounded-lg bg-white">
                <LogOut size={13} /> Disconnect
              </button>
            </div>

            {st.needs_reconnect ? (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-[13px] text-amber-800 flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2"><AlertTriangle size={14} /> The Microsoft sign-in expired. Reconnect to keep syncing.</span>
                <button onClick={connect} className="h-8 px-3 bg-amber-600 text-white rounded-lg text-[12.5px] font-semibold">Reconnect</button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1.5 block">Plan</label>
                    <select value={st.plan_id || ""} disabled={busy || plans === null}
                      onChange={(e) => { const p = (plans || []).find((x) => x.id === e.target.value); save({ plan_id: p?.id, plan_title: p?.title, bucket_id: "", bucket_name: "" }); }}
                      className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] bg-white" data-testid="planner-plan">
                      <option value="">{plans === null ? "Loading plans…" : plans.length ? "Choose a plan…" : "No plans found"}</option>
                      {(plans || []).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                    </select>
                    {plans && plans.length === 0 && <p className="text-[11.5px] text-slate-400 mt-1">Create a plan in Planner (or ask to be added to one), then reload this page.</p>}
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1.5 block">Bucket</label>
                    <select value={st.bucket_id || ""} disabled={busy || !st.plan_id || buckets === null}
                      onChange={(e) => { const b = (buckets || []).find((x) => x.id === e.target.value); save({ bucket_id: b?.id || "", bucket_name: b?.name || "" }); }}
                      className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] bg-white" data-testid="planner-bucket">
                      <option value="">{!st.plan_id ? "Choose a plan first" : "Planner's default bucket"}</option>
                      {(buckets || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="flex items-start justify-between gap-4 pt-1">
                  <div>
                    <div className="text-[13.5px] font-semibold text-slate-900">Sync tasks to Planner</div>
                    <p className="text-[12.5px] text-slate-500 mt-0.5">New and approved tasks are added within a minute; changes in Planner come back every 5 minutes.</p>
                    {!st.enabled && (
                      <label className="flex items-center gap-2 mt-2 text-[12.5px] text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={includeExisting} onChange={(e) => setIncludeExisting(e.target.checked)} className="h-4 w-4 accent-emerald-600" />
                        Also add my current open tasks
                      </label>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                    <input type="checkbox" checked={!!st.enabled} disabled={busy || !st.plan_id}
                      onChange={(e) => save({ enabled: e.target.checked, include_existing: e.target.checked && includeExisting }, e.target.checked ? "Planner sync switched on" : "Planner sync paused")}
                      className="sr-only peer" data-testid="planner-enabled" />
                    <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-emerald-600 peer-disabled:opacity-50 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
                  </label>
                </div>

                {st.enabled && (
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100">
                    <div className="text-[12.5px] text-slate-500">
                      {st.last_error
                        ? <span className="text-red-600 inline-flex items-center gap-1.5"><AlertTriangle size={13} /> {st.last_error}</span>
                        : <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-600" /> {st.synced_tasks || 0} tasks linked · last sync {ago(st.last_sync_at)}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {!String(st.sync_from || "").startsWith("1970") && (
                        <button onClick={addExisting} disabled={busy} className="h-8 px-3 inline-flex items-center gap-1.5 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-lg text-[12.5px] font-medium" data-testid="planner-add-existing">
                          Add my current open tasks
                        </button>
                      )}
                      <button onClick={syncNow} disabled={busy} className="h-8 px-3 inline-flex items-center gap-1.5 border border-slate-200 hover:border-slate-400 rounded-lg text-[12.5px] font-medium">
                        <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Sync now
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
